import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Typography,
  Chip,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Grid,
  Card,
  CardContent,
  Divider,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Clear as ClearIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Psychology as SkillIcon,
  Info as InfoIcon,
  OpenInFull as OpenInFullIcon
} from '@mui/icons-material';
import XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getCandidateDetailsReport, getCandidateImage } from '../services/onboardingapi';
import { getEmployeeImage } from '../services/HRstaff';
import { IMAGE_BASE_URL } from '../config';
import marabesLogo from '../assets/images/logos/Marabes.png';
import axopeopleLogo from '../assets/images/logos/Axopeople.png';

if (typeof jsPDF.prototype.autoTable !== 'function') {
  jsPDF.prototype.autoTable = autoTable;
}

// === THEME PALETTE ===
const THEME = {
  bg: '#F8FAFC',
  surface: '#FFFFFF',
  primary: '#1E293B',
  secondary: '#64748B',
  accent: '#3B82F6',
  accentDark: '#1D4ED8',
  accentLight: '#EFF6FF',
  border: '#E2E8F0',
  success: '#10B981',
  successLight: '#ECFDF5',
  warning: '#F59E0B',
  warningLight: '#FFFBEB',
  purple: '#8B5CF6',
  purpleLight: '#F5F3FF',
  tableHeader: '#F1F5F9'
};

const styles = {
  container: {
    minHeight: '100vh',
    padding: { xs: '16px', md: '24px' },
    // backgroundColor: THEME.bg,
    fontFamily: '"Inter", "Segoe UI", Roboto, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  logoBanner: {
    backgroundColor: '#F3F4F6',
    borderRadius: '14px',
    border: `1px solid ${THEME.border}`,
    px: { xs: 2, md: 3 },
    py: 1.5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
  },
  logoBannerTitle: {
    fontSize: { xs: '0.95rem', sm: '1.15rem', md: '1.3rem' },
    fontWeight: '800',
    color: '#0F2C59',
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    flexGrow: 1
  },
  headerCard: {
    backgroundColor: THEME.surface,
    padding: { xs: '20px', md: '24px' },
    borderRadius: '16px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04), 0 2px 4px -1px rgba(0,0,0,0.02)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: `1px solid ${THEME.border}`,
    flexWrap: 'wrap',
    gap: '16px'
  },
  title: {
    fontSize: { xs: '1.25rem', md: '1.5rem' },
    fontWeight: '800',
    color: THEME.primary,
    letterSpacing: '-0.025em',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  subTitle: {
    fontSize: '0.875rem',
    color: THEME.secondary,
    marginTop: '4px'
  },
  statCard: {
    backgroundColor: THEME.surface,
    padding: '20px',
    borderRadius: '16px',
    border: `1px solid ${THEME.border}`,
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 16px rgba(0,0,0,0.06)'
    }
  },
  iconBox: (bg, color) => ({
    width: 48,
    height: 48,
    borderRadius: '12px',
    backgroundColor: bg,
    color: color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }),
  filterCard: {
    backgroundColor: THEME.surface,
    padding: '16px 20px',
    borderRadius: '16px',
    border: `1px solid ${THEME.border}`,
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
  },
  tableCard: {
    backgroundColor: THEME.surface,
    borderRadius: '16px',
    border: `1px solid ${THEME.border}`,
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)',
    overflow: 'hidden'
  },
  th: {
    backgroundColor: THEME.tableHeader,
    color: THEME.primary,
    fontWeight: '700',
    fontSize: '0.78rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: '14px 16px',
    borderBottom: `2px solid ${THEME.border}`,
    whiteSpace: 'nowrap'
  },
  td: {
    padding: '12px 16px',
    color: '#334155',
    fontSize: '0.875rem',
    borderBottom: `1px solid ${THEME.border}`
  },
  candidateCard: {
    backgroundColor: THEME.surface,
    borderRadius: '16px',
    border: `1px solid ${THEME.border}`,
    boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
    transition: 'all 0.25s ease',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 24px rgba(0,0,0,0.08)',
      borderColor: THEME.accent
    }
  },
  richHtmlContent: {
    fontSize: '0.9rem',
    lineHeight: 1.65,
    color: '#334155',
    '& p': { margin: '0 0 10px 0' },
    '& p:last-child': { margin: 0 },
    '& ul, & ol': { margin: '4px 0 10px 20px', paddingLeft: '8px' },
    '& li': { margin: '3px 0' },
    '& b, & strong': { fontWeight: 700, color: '#1E293B' },
    '& em, & i': { fontStyle: 'italic' },
    '& h1, & h2, & h3, & h4': { margin: '12px 0 6px 0', color: '#0F172A', fontWeight: 700 },
    '& br': { display: 'block', content: '""', marginTop: '4px' }
  }
};

// === HTML & ASSET HELPERS ===
const isHtml = (str) => typeof str === 'string' && /<[a-z][\s\S]*>/i.test(str);

const stripHtml = (html) => {
  if (!html) return '';
  if (typeof html !== 'string') return String(html);
  const processed = html
    .replace(/<br\s*[\/]?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<\/li>/gi, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
  const tmp = document.createElement('div');
  tmp.innerHTML = processed;
  return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
};

const getBase64FromUrl = async (url) => {
  if (!url) return null;
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    // Fallback: try loading via HTML Image element
    return new Promise((resolve) => {
      try {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth || img.width || 64;
            canvas.height = img.naturalHeight || img.height || 64;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
          } catch (e) {
            resolve(null);
          }
        };
        img.onerror = () => resolve(null);
        img.src = url;
      } catch (err) {
        resolve(null);
      }
    });
  }
};

const createInitialsAvatarBase64 = (name) => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Background circle
    ctx.fillStyle = getAvatarColor(name);
    ctx.beginPath();
    ctx.arc(32, 32, 30, 0, Math.PI * 2);
    ctx.fill();

    // Initials text
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 24px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(getInitials(name), 32, 33);

    return canvas.toDataURL('image/png');
  } catch (e) {
    return null;
  }
};

// Date Parsing Helper
const parseDate = (dateVal) => {
  if (!dateVal) return null;
  if (Array.isArray(dateVal)) {
    const [year, month, day] = dateVal;
    return new Date(year, month - 1, day);
  }
  if (typeof dateVal === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateVal)) {
    const [year, month, day] = dateVal.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(dateVal);
  return isNaN(parsed.getTime()) ? null : parsed;
};

// Date Formatter Helper (DD/MM/YYYY)
const formatDisplayDate = (dateVal) => {
  const d = parseDate(dateVal);
  if (!d) return 'N/A';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

// Candidate Avatar Color
const getAvatarColor = (name) => {
  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#6366F1'];
  if (!name) return colors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const getInitials = (name) => {
  if (!name) return 'C';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Candidate Image URL Helper
const getCandidateImageUrl = (candidate) => {
  if (!candidate) return '';
  const pic = candidate.picture || candidate.image;
  if (!pic) return '';
  if (pic.startsWith('http://') || pic.startsWith('https://') || pic.startsWith('blob:') || pic.startsWith('data:')) {
    return pic;
  }
  return `${IMAGE_BASE_URL}${pic}`;
};

// Helper to crop an image to a centered circle in base64 format for PDF export
const makeCircularImageBase64 = (imgSource) => {
  if (!imgSource) return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      try {
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        const size = Math.min(w, h);
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(imgSource);

        // Circular clipping path
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();

        // Center crop
        const sx = (w - size) / 2;
        const sy = (h - size) / 2;
        ctx.drawImage(img, sx, sy, size, size, 0, 0, size, size);

        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        resolve(imgSource);
      }
    };
    img.onerror = () => resolve(imgSource);
    img.src = imgSource;
  });
};

// Helper to fetch base64 image with multiple fallbacks for PDF exports
const fetchCandidateBase64 = async (item) => {
  if (!item) return null;

  // 1. Try getEmployeeImage if employeeId is present
  if (item.employeeId) {
    try {
      const blobUrl = await getEmployeeImage(item.employeeId);
      if (blobUrl) {
        const b64 = await getBase64FromUrl(blobUrl);
        if (b64) return await makeCircularImageBase64(b64);
      }
    } catch (e) { }
  }

  // 2. Try getEmployeeImage with candidateId
  if (item.candidateId) {
    try {
      const blobUrl = await getEmployeeImage(item.candidateId);
      if (blobUrl) {
        const b64 = await getBase64FromUrl(blobUrl);
        if (b64) return await makeCircularImageBase64(b64);
      }
    } catch (e) { }
  }

  // 3. Try getCandidateImage with candidateId
  if (item.candidateId) {
    try {
      const blobUrl = await getCandidateImage(item.candidateId);
      if (blobUrl) {
        const b64 = await getBase64FromUrl(blobUrl);
        if (b64) return await makeCircularImageBase64(b64);
      }
    } catch (e) { }
  }

  // 4. Try direct Azure URLs if picture filename is present
  const pic = item.picture || item.image;
  if (pic) {
    if (pic.startsWith('http://') || pic.startsWith('https://') || pic.startsWith('blob:') || pic.startsWith('data:')) {
      const b64 = await getBase64FromUrl(pic);
      if (b64) return await makeCircularImageBase64(b64);
    } else {
      const b64Img = await getBase64FromUrl(`https://axopeople.blob.core.windows.net/images/${pic}`);
      if (b64Img) return await makeCircularImageBase64(b64Img);

      const b64Cand = await getBase64FromUrl(`https://axopeople.blob.core.windows.net/imagecandidate/${pic}`);
      if (b64Cand) return await makeCircularImageBase64(b64Cand);
    }
  }

  // 5. Fallback to initials badge (already circular)
  return createInitialsAvatarBase64(item.name);
};

// Dedicated Candidate Avatar component with multi-container fallback & initials badge
const CandidateAvatar = ({ candidate, size = 40, fontSize = '0.875rem' }) => {
  const [imgError, setImgError] = useState(false);
  const [containerIdx, setContainerIdx] = useState(0);

  const pic = candidate?.picture || candidate?.image;
  const name = candidate?.name || '';
  const initials = getInitials(name);
  const bgColor = getAvatarColor(name);

  const getSrc = () => {
    if (!pic) return null;
    if (pic.startsWith('http://') || pic.startsWith('https://') || pic.startsWith('blob:') || pic.startsWith('data:')) {
      return pic;
    }
    const containers = [
      'https://axopeople.blob.core.windows.net/images/',
      'https://axopeople.blob.core.windows.net/imagecandidate/'
    ];
    return `${containers[containerIdx]}${pic}`;
  };

  const src = getSrc();

  const handleImgError = () => {
    if (pic && !pic.startsWith('http') && containerIdx === 0) {
      setContainerIdx(1);
    } else {
      setImgError(true);
    }
  };

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: bgColor,
        color: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: fontSize,
        overflow: 'hidden',
        flexShrink: 0,
        border: '2px solid #FFFFFF',
        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
        position: 'relative'
      }}
    >
      {src && !imgError ? (
        <Box
          component="img"
          src={src}
          alt={name}
          onError={handleImgError}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '50%'
          }}
        />
      ) : (
        initials
      )}
    </Box>
  );
};

/**
 * Compact HTML table cell with preview and expandable popup
 */
const CompactHtmlCell = ({ value, title, candidateName, icon: IconComponent, iconColor, onOpenDialog }) => {
  if (!value || (typeof value === 'string' && !value.trim())) {
    return <Typography sx={{ color: THEME.secondary, fontSize: '0.85rem' }}>None</Typography>;
  }

  const plainText = stripHtml(value);
  const isLong = plainText.length > 70 || isHtml(value);

  return (
    <Box
      onClick={() => onOpenDialog({ title, candidateName, content: value, icon: IconComponent, iconColor })}
      sx={{
        cursor: 'pointer',
        p: 0.75,
        borderRadius: '8px',
        transition: 'all 0.15s ease',
        '&:hover': {
          backgroundColor: '#F1F5F9',
          '& .expand-btn': { opacity: 1 }
        }
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        {IconComponent && (
          <IconComponent
            sx={{
              fontSize: '1.05rem',
              color: iconColor || THEME.secondary,
              mt: 0.2,
              flexShrink: 0
            }}
          />
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '0.82rem',
              color: THEME.primary,
              lineHeight: 1.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-word'
            }}
          >
            {plainText}
          </Typography>
        </Box>
        {isLong && (
          <Tooltip title="View full content">
            <IconButton
              size="small"
              className="expand-btn"
              sx={{
                p: 0.25,
                opacity: 0.4,
                color: THEME.secondary,
                flexShrink: 0,
                transition: 'opacity 0.15s'
              }}
            >
              <OpenInFullIcon sx={{ fontSize: '0.78rem' }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};

const CandidateDetailsReport = () => {
  // State
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('ALL');
  const [selectedActiveStatus, setSelectedActiveStatus] = useState('ALL'); // 'ALL' | 'ACTIVE' | 'INACTIVE'
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // View & Pagination
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState('joiningDate');
  const [sortOrder, setSortOrder] = useState('desc');

  // Selected candidate for full detail dossier modal
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // HTML Reader Dialog State (for viewing big sections)
  const [htmlDialog, setHtmlDialog] = useState({
    open: false,
    title: '',
    candidateName: '',
    content: '',
    icon: null,
    iconColor: null
  });

  // Candidate Images Map (blob URLs fetched via getEmployeeImage / getCandidateImage)
  const [candidateImages, setCandidateImages] = useState({});

  // Asynchronously load candidate/employee images via getEmployeeImage / getCandidateImage
  useEffect(() => {
    let isMounted = true;
    const loadImages = async () => {
      const newImages = {};
      for (const item of reportData) {
        const key = item.employeeId || item.candidateId;
        if (!key || candidateImages[key]) continue;

        try {
          let imgBlobUrl = null;
          if (item.employeeId) {
            imgBlobUrl = await getEmployeeImage(item.employeeId);
          }
          if (!imgBlobUrl && item.candidateId) {
            imgBlobUrl = await getEmployeeImage(item.candidateId);
          }
          if (!imgBlobUrl && item.candidateId) {
            try {
              imgBlobUrl = await getCandidateImage(item.candidateId);
            } catch (e) { }
          }
          if (imgBlobUrl && isMounted) {
            newImages[key] = imgBlobUrl;
            if (item.candidateId) newImages[item.candidateId] = imgBlobUrl;
            if (item.employeeId) newImages[item.employeeId] = imgBlobUrl;
          }
        } catch (err) {
          // Ignore individual load failure
        }
      }
      if (isMounted && Object.keys(newImages).length > 0) {
        setCandidateImages((prev) => ({ ...prev, ...newImages }));
      }
    };

    if (reportData && reportData.length > 0) {
      loadImages();
    }

    return () => {
      isMounted = false;
    };
  }, [reportData]);

  // Helper to get avatar source
  const getCandidateAvatarSrc = useCallback(
    (candidate) => {
      if (!candidate) return '';
      const empId = candidate.employeeId;
      const candId = candidate.candidateId;
      if (empId && candidateImages[empId]) return candidateImages[empId];
      if (candId && candidateImages[candId]) return candidateImages[candId];
      return getCandidateImageUrl(candidate);
    },
    [candidateImages]
  );

  // Fetch report data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCandidateDetailsReport();
      const list = Array.isArray(data) ? data : [];
      setReportData(list);
    } catch (err) {
      console.error('Error loading candidate report:', err);
      setError(err.message || 'Failed to fetch candidate details report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Unique locations from data
  const locations = useMemo(() => {
    const locSet = new Set();
    reportData.forEach((item) => {
      if (item.location && item.location.trim()) {
        locSet.add(item.location.trim());
      }
    });
    return Array.from(locSet).sort();
  }, [reportData]);

  // Filtered and Sorted Data
  const filteredData = useMemo(() => {
    return reportData
      .filter((item) => {
        // Location Filter
        if (selectedLocation !== 'ALL' && item.location !== selectedLocation) {
          return false;
        }

        // Active Status Filter
        if (selectedActiveStatus === 'ACTIVE' && item.isActive !== true) {
          return false;
        }
        if (selectedActiveStatus === 'INACTIVE' && item.isActive === true) {
          return false;
        }

        // Date Range Filter
        const jDate = parseDate(item.joiningDate);
        if (startDate && jDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          if (jDate < start) return false;
        }
        if (endDate && jDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          if (jDate > end) return false;
        }

        // Search Term Filter (Name, CandidateId, Location, Academic, Experience, Skills)
        if (searchTerm.trim()) {
          const query = searchTerm.toLowerCase().trim();
          const name = (item.name || '').toLowerCase();
          const id = (item.candidateId || '').toLowerCase();
          const loc = (item.location || '').toLowerCase();
          const acad = stripHtml(item.academicBackground || '').toLowerCase();
          const exp = stripHtml(item.professionalExperience || '').toLowerCase();
          const skillsText = (item.communicationSkills || [])
            .map((s) => (s.description || '').toLowerCase())
            .join(' ');

          const matches =
            name.includes(query) ||
            id.includes(query) ||
            loc.includes(query) ||
            acad.includes(query) ||
            exp.includes(query) ||
            skillsText.includes(query);

          if (!matches) return false;
        }

        return true;
      })
      .sort((a, b) => {
        let valA = a[sortField];
        let valB = b[sortField];

        if (sortField === 'joiningDate') {
          valA = parseDate(a.joiningDate) ? parseDate(a.joiningDate).getTime() : 0;
          valB = parseDate(b.joiningDate) ? parseDate(b.joiningDate).getTime() : 0;
        } else if (sortField === 'isActive') {
          valA = a.isActive ? 1 : 0;
          valB = b.isActive ? 1 : 0;
        } else {
          valA = stripHtml((valA || '').toString()).toLowerCase();
          valB = stripHtml((valB || '').toString()).toLowerCase();
        }

        if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
        if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
  }, [reportData, selectedLocation, selectedActiveStatus, startDate, endDate, searchTerm, sortField, sortOrder]);

  // Pagination slice
  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  // Sorting Handler
  const handleSort = (field) => {
    const isAsc = sortField === field && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedLocation('ALL');
    setSelectedActiveStatus('ALL');
    setStartDate(null);
    setEndDate(null);
    setPage(0);
  };

  // Open candidate dossier modal
  const handleOpenDetail = (candidate) => {
    setSelectedCandidate(candidate);
    setDetailModalOpen(true);
  };

  // Open HTML Reader Dialog for a specific field
  const handleOpenHtmlDialog = ({ title, candidateName, content, icon, iconColor }) => {
    setHtmlDialog({
      open: true,
      title: title || 'Details',
      candidateName: candidateName || '',
      content: content || '',
      icon: icon || null,
      iconColor: iconColor || THEME.accent
    });
  };

  const handleCloseHtmlDialog = () => {
    setHtmlDialog((prev) => ({ ...prev, open: false }));
  };

  // KPI calculations
  const totalCandidates = reportData.length;
  const filteredCandidatesCount = filteredData.length;
  const activeCandidatesCount = useMemo(() => {
    return reportData.filter((c) => c.isActive === true).length;
  }, [reportData]);
  const totalSkillsCount = useMemo(() => {
    return reportData.reduce((acc, c) => acc + (c.communicationSkills?.length || 0), 0);
  }, [reportData]);
  const masterDegreesCount = useMemo(() => {
    return reportData.filter((c) =>
      stripHtml(c.academicBackground || '').toLowerCase().includes('master')
    ).length;
  }, [reportData]);
  const candidatesWithDegreeCount = useMemo(() => {
    return reportData.filter((c) =>
      Boolean(stripHtml(c.academicBackground || '').trim())
    ).length;
  }, [reportData]);

  // Export to Excel (.xlsx)
  const handleExportExcel = () => {
    const rows = filteredData.map((item) => {
      const skillsStr = (item.communicationSkills || [])
        .map((s) => s.description)
        .filter(Boolean)
        .join(', ');

      return {
        'Photo': getCandidateImageUrl(item) || 'N/A',
        'Candidate Name': item.name || 'N/A',
        'Status': item.isActive ? 'Active' : 'Inactive',
        'Joining Date': formatDisplayDate(item.joiningDate),
        'Location': item.location || 'N/A',
        'Academic Background': stripHtml(item.academicBackground) || 'N/A',
        'Professional Experience': stripHtml(item.professionalExperience) || 'N/A',
        'Communication Skills': skillsStr || 'None'
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);

    // Styling headers & cells
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const address = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!worksheet[address]) continue;
      worksheet[address].s = {
        fill: { fgColor: { rgb: '1E293B' } },
        font: { color: { rgb: 'FFFFFF' }, bold: true, sz: 11 },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: {
          top: { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'medium', color: { rgb: '3B82F6' } },
          left: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right: { style: 'thin', color: { rgb: 'CCCCCC' } }
        }
      };
    }

    // Set Column Widths
    worksheet['!cols'] = [
      { wch: 32 }, // Photo
      { wch: 24 }, // Candidate Name
      { wch: 12 }, // Status
      { wch: 15 }, // Joining Date
      { wch: 18 }, // Location
      { wch: 32 }, // Academic Background
      { wch: 36 }, // Professional Experience
      { wch: 35 }  // Communication Skills
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Candidates Details');
    const filename = `Candidate_Details_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  // Export to PDF (.pdf) with logos banner
  const handleExportPDF = async () => {
    try {
      const doc = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;

      const axoLogoUrl = '/cLogo.png';
      const [axoBase64, marabesBase64] = await Promise.all([
        getBase64FromUrl(axoLogoUrl).then((res) => res || getBase64FromUrl(axopeopleLogo)),
        getBase64FromUrl(marabesLogo)
      ]);

      // Pre-fetch all candidate photos in base64 using getEmployeeImage / getCandidateImage / azure blob
      const candidateBase64Map = {};
      await Promise.all(
        filteredData.map(async (item) => {
          const key = item.candidateId || item.employeeId || item.name;
          const b64 = await fetchCandidateBase64(item);
          if (b64) {
            candidateBase64Map[key] = b64;
          }
        })
      );

      // PDF HEADER WITH LOGOS
      const addHeader = (pageNum) => {
        if (pageNum > 1) doc.addPage();

        // Banner box
        doc.setFillColor(243, 244, 246);
        doc.rect(0, 0, pageWidth, 22, 'F');

        // Left Logo: Marabes
        try {
          if (marabesBase64) {
            doc.addImage(marabesBase64, 'PNG', margin, 5, 28, 12);
          }
        } catch (e) { }

        // Right Logo: AXO
        try {
          if (axoBase64) {
            doc.addImage(axoBase64, 'PNG', pageWidth - margin - 26, 5, 26, 12);
          }
        } catch (e) { }

        // Center Title
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 44, 89);
        doc.text('RECRUITMENT & CANDIDATE DETAILS REPORT', pageWidth / 2, 14, { align: 'center' });
      };

      addHeader(1);

      const tableColumns = [
        { header: 'Photo', dataKey: 'photo' },
        { header: 'Candidate Name', dataKey: 'name' },
        { header: 'Status', dataKey: 'status' },
        { header: 'Joining Date', dataKey: 'joiningDate' },
        { header: 'Location', dataKey: 'location' },
        { header: 'Academic Background', dataKey: 'academic' },
        { header: 'Experience', dataKey: 'experience' },
        { header: 'Communication Skills', dataKey: 'skills' }
      ];

      const tableData = filteredData.map((item) => {
        const skillsStr = (item.communicationSkills || [])
          .map((s) => s.description)
          .filter(Boolean)
          .join(', ');

        return {
          _rawItem: item,
          photo: '',
          name: item.name || 'N/A',
          status: item.isActive ? 'Active' : 'Inactive',
          joiningDate: formatDisplayDate(item.joiningDate),
          location: item.location || 'N/A',
          academic: stripHtml(item.academicBackground) || 'N/A',
          experience: stripHtml(item.professionalExperience) || 'N/A',
          skills: skillsStr || 'None'
        };
      });

      autoTable(doc, {
        startY: 28,
        columns: tableColumns,
        body: tableData,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 3.5,
          valign: 'middle',
          overflow: 'linebreak',
          minCellHeight: 14
        },
        headStyles: {
          fillColor: [30, 41, 59],
          textColor: 255,
          fontSize: 8.5,
          fontStyle: 'bold',
          halign: 'left'
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252]
        },
        columnStyles: {
          0: { cellWidth: 16, halign: 'center' },
          1: { cellWidth: 35, fontStyle: 'bold' },
          2: { cellWidth: 18, halign: 'center' },
          3: { cellWidth: 22 },
          4: { cellWidth: 24 },
          5: { cellWidth: 48 },
          6: { cellWidth: 54 },
          7: { cellWidth: 54 }
        },
        didDrawCell: (data) => {
          if (data.section === 'body' && data.column.dataKey === 'photo') {
            const raw = data.row.raw?._rawItem || filteredData[data.row.index];
            if (raw) {
              const key = raw.candidateId || raw.employeeId || raw.name;
              const b64 = candidateBase64Map[key];
              if (b64) {
                try {
                  const imgSize = 10; // 10mm x 10mm
                  const posX = data.cell.x + (data.cell.width - imgSize) / 2;
                  const posY = data.cell.y + (data.cell.height - imgSize) / 2;
                  doc.addImage(b64, 'PNG', posX, posY, imgSize, imgSize);
                } catch (e) {
                  console.warn('Error rendering image in PDF table:', e);
                }
              }
            }
          }
        },
        didDrawPage: (data) => {
          const str = `Page ${doc.internal.getNumberOfPages()} | Total: ${filteredData.length} records`;
          doc.setFontSize(7.5);
          doc.setTextColor(148, 163, 184);
          doc.text(str, pageWidth - 60, pageHeight - 8);
        }
      });

      doc.save(`Candidate_Details_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('Failed to export PDF:', err);
      alert('Failed to generate PDF: ' + err.message);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={styles.container}>
        {/* === BRANDING & LOGOS HEADER BANNER === */}
        {/* <Box sx={styles.logoBanner}> */}
        {/* Left: Marabes Logo */}
        {/* <Box sx={{ display: 'flex', alignItems: 'center', minWidth: { xs: 80, sm: 130 } }}>
            <img
              src={marabesLogo}
              alt="Marabes"
              style={{ height: '36px', maxWidth: '140px', objectFit: 'contain' }}
            />
          </Box> */}

        {/* Center: Title */}
        {/* <Typography sx={styles.logoBannerTitle}>
            RECRUITMENT & CANDIDATE DETAILS REPORT
          </Typography> */}

        {/* Right: Axo Logo */}
        {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minWidth: { xs: 80, sm: 130 } }}>
            <img
              src={axopeopleLogo}
              alt="AXO"
              style={{ height: '32px', maxWidth: '140px', objectFit: 'contain' }}
            />
          </Box> */}
        {/* </Box> */}

        {/* HEADER CONTROLS SECTION */}
        <Box sx={styles.headerCard}>
          <Box>
            <Typography sx={styles.title}>
              <CheckCircleIcon sx={{ color: THEME.accent, fontSize: '1.75rem' }} />
              Candidate Details Report
            </Typography>
            <Typography sx={styles.subTitle}>
              Comprehensive profile, academic qualifications, experience, and communication skills of hired candidates
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            {/* View Mode Switcher */}
            <Tooltip title="Table View">
              <IconButton
                size="small"
                onClick={() => setViewMode('table')}
                sx={{
                  border: `1px solid ${THEME.border}`,
                  backgroundColor: viewMode === 'table' ? THEME.accentLight : 'transparent',
                  color: viewMode === 'table' ? THEME.accent : THEME.secondary,
                  borderRadius: '8px',
                  p: 1
                }}
              >
                <ViewListIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Card Grid View">
              <IconButton
                size="small"
                onClick={() => setViewMode('grid')}
                sx={{
                  border: `1px solid ${THEME.border}`,
                  backgroundColor: viewMode === 'grid' ? THEME.accentLight : 'transparent',
                  color: viewMode === 'grid' ? THEME.accent : THEME.secondary,
                  borderRadius: '8px',
                  p: 1
                }}
              >
                <GridViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Refresh Button */}
            <Tooltip title="Refresh Data">
              <IconButton
                size="small"
                onClick={fetchData}
                disabled={loading}
                sx={{
                  border: `1px solid ${THEME.border}`,
                  color: THEME.secondary,
                  borderRadius: '8px',
                  p: 1,
                  '&:hover': { color: THEME.accent, backgroundColor: THEME.accentLight }
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Export to Excel */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<ExcelIcon />}
              onClick={handleExportExcel}
              disabled={loading || filteredData.length === 0}
              sx={{
                borderColor: '#10B981',
                color: '#10B981',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                px: 1.5,
                py: 0.75,
                '&:hover': {
                  borderColor: '#059669',
                  backgroundColor: '#ECFDF5'
                }
              }}
            >
              Export Excel
            </Button>

            {/* Export to PDF */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<PdfIcon />}
              onClick={handleExportPDF}
              disabled={loading || filteredData.length === 0}
              sx={{
                borderColor: '#EF4444',
                color: '#EF4444',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                px: 1.5,
                py: 0.75,
                '&:hover': {
                  borderColor: '#DC2626',
                  backgroundColor: '#FEF2F2'
                }
              }}
            >
              Export PDF
            </Button>
          </Stack>
        </Box>

        {/* METRICS / STATS CARDS */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={styles.statCard}>
              <Box sx={styles.iconBox(THEME.accentLight, THEME.accent)}>
                <PersonIcon />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.8rem', color: THEME.secondary, fontWeight: 600 }}>
                  Total Candidates
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: THEME.primary }}>
                  {totalCandidates}{' '}
                  <Typography component="span" sx={{ fontSize: '0.8rem', color: THEME.success, fontWeight: 700 }}>
                    ({activeCandidatesCount} Active)
                  </Typography>
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box sx={styles.statCard}>
              <Box sx={styles.iconBox(THEME.successLight, THEME.success)}>
                <LocationIcon />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.8rem', color: THEME.secondary, fontWeight: 600 }}>
                  Locations & Offices
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: THEME.primary }}>
                  {locations.length}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box sx={styles.statCard}>
              <Box sx={styles.iconBox(THEME.purpleLight, THEME.purple)}>
                <SchoolIcon />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.8rem', color: THEME.secondary, fontWeight: 600 }}>
                  Master's Degrees
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: THEME.primary }}>
                  {masterDegreesCount}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box sx={styles.statCard}>
              <Box sx={styles.iconBox(THEME.warningLight, THEME.warning)}>
                <SkillIcon />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.8rem', color: THEME.secondary, fontWeight: 600 }}>
                  Communication Skills
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: THEME.primary }}>
                  {totalSkillsCount}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* FILTER CONTROLS */}
        <Box sx={styles.filterCard}>
          <Grid container spacing={2} alignItems="center">
            {/* Search Input */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search candidate name, degree, skill..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: THEME.secondary, fontSize: '1.2rem' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { borderRadius: '10px', backgroundColor: '#FFFFFF', fontSize: '0.875rem' }
                }}
              />
            </Grid>

            {/* Location Select */}
            <Grid item sx={{ minWidth: 170 }} xs={12} sm={6} md={2.2}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.875rem' }}>Location / Office</InputLabel>
                <Select
                  value={selectedLocation}
                  label="Location / Office"
                  onChange={(e) => {
                    setSelectedLocation(e.target.value);
                    setPage(0);
                  }}
                  sx={{ borderRadius: '10px', backgroundColor: '#FFFFFF', fontSize: '0.875rem' }}
                >
                  <MenuItem value="ALL">All Locations ({locations.length})</MenuItem>
                  {locations.map((loc) => (
                    <MenuItem key={loc} value={loc}>
                      {loc}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Active Status Select */}
            <Grid item sx={{ minWidth: 150 }} xs={12} sm={6} md={1.8}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.875rem' }}>Status</InputLabel>
                <Select
                  value={selectedActiveStatus}
                  label="Status"
                  onChange={(e) => {
                    setSelectedActiveStatus(e.target.value);
                    setPage(0);
                  }}
                  sx={{ borderRadius: '10px', backgroundColor: '#FFFFFF', fontSize: '0.875rem' }}
                >
                  <MenuItem value="ALL">All Statuses ({reportData.length})</MenuItem>
                  <MenuItem value="ACTIVE">Active Only ({activeCandidatesCount})</MenuItem>
                  <MenuItem value="INACTIVE">Inactive Only ({reportData.length - activeCandidatesCount})</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Joining Date Range Start */}
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="Start (From)"
                value={startDate}
                onChange={(newVal) => {
                  setStartDate(newVal);
                  setPage(0);
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      borderRadius: '10px',
                      backgroundColor: '#FFFFFF',
                      '& .MuiInputBase-root': { borderRadius: '10px' }
                    }
                  }
                }}
              />
            </Grid>

            {/* Joining Date Range End */}
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="End (To)"
                value={endDate}
                onChange={(newVal) => {
                  setEndDate(newVal);
                  setPage(0);
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      borderRadius: '10px',
                      backgroundColor: '#FFFFFF',
                      '& .MuiInputBase-root': { borderRadius: '10px' }
                    }
                  }
                }}
              />
            </Grid>

            {/* Reset Filters */}
            <Grid item xs={12} sm={6} md={1}>
              <Button
                fullWidth
                variant="text"
                size="small"
                onClick={handleClearFilters}
                startIcon={<ClearIcon />}
                sx={{
                  color: THEME.secondary,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  '&:hover': { color: THEME.primary }
                }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* ERROR STATE */}
        {error && (
          <Alert severity="error" sx={{ borderRadius: '12px' }}>
            {error}
          </Alert>
        )}

        {/* LOADING STATE */}
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
            <CircularProgress size={40} sx={{ color: THEME.accent }} />
            <Typography sx={{ mt: 2, color: THEME.secondary, fontSize: '0.9rem' }}>
              Loading candidate details report...
            </Typography>
          </Box>
        ) : filteredData.length === 0 ? (
          /* EMPTY STATE */
          <Paper sx={{ ...styles.tableCard, py: 8, textAlign: 'center', px: 2 }}>
            <InfoIcon sx={{ fontSize: '3rem', color: THEME.secondary, mb: 1.5 }} />
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: THEME.primary }}>
              No candidates found
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: THEME.secondary, mt: 0.5, maxWidth: 450, mx: 'auto' }}>
              No candidate records match your current filter criteria. Try adjusting or clearing your filters.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
              sx={{ mt: 2, textTransform: 'none', borderRadius: '8px' }}
            >
              Clear All Filters
            </Button>
          </Paper>
        ) : viewMode === 'table' ? (
          /* TABLE VIEW */
          <Box sx={styles.tableCard}>
            <TableContainer>
              <Table sx={{ minWidth: 950 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={styles.th}>
                      <TableSortLabel
                        active={sortField === 'name'}
                        direction={sortField === 'name' ? sortOrder : 'asc'}
                        onClick={() => handleSort('name')}
                      >
                        Candidate
                      </TableSortLabel>
                    </TableCell>

                    <TableCell sx={{ ...styles.th, textAlign: 'center', width: 100 }}>
                      <TableSortLabel
                        active={sortField === 'isActive'}
                        direction={sortField === 'isActive' ? sortOrder : 'asc'}
                        onClick={() => handleSort('isActive')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>

                    <TableCell sx={styles.th}>
                      <TableSortLabel
                        active={sortField === 'joiningDate'}
                        direction={sortField === 'joiningDate' ? sortOrder : 'asc'}
                        onClick={() => handleSort('joiningDate')}
                      >
                        Joining Date
                      </TableSortLabel>
                    </TableCell>

                    <TableCell sx={styles.th}>
                      <TableSortLabel
                        active={sortField === 'location'}
                        direction={sortField === 'location' ? sortOrder : 'asc'}
                        onClick={() => handleSort('location')}
                      >
                        Location
                      </TableSortLabel>
                    </TableCell>

                    <TableCell sx={{ ...styles.th, minWidth: 200 }}>Academic Background</TableCell>
                    <TableCell sx={{ ...styles.th, minWidth: 220 }}>Professional Experience</TableCell>
                    <TableCell sx={{ ...styles.th, minWidth: 180 }}>Communication Skills</TableCell>
                    <TableCell sx={{ ...styles.th, textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedData.map((candidate) => {
                    const skills = candidate.communicationSkills || [];

                    return (
                      <TableRow
                        key={candidate.candidateId || candidate.name}
                        hover
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          transition: 'background-color 0.15s ease'
                        }}
                      >
                        {/* Candidate Avatar & Name */}
                        <TableCell sx={styles.td}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <CandidateAvatar candidate={candidate} size={40} fontSize="0.875rem" />
                            <Box>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: THEME.primary }}>
                                {candidate.name || 'Unnamed Candidate'}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* Status (Active / Inactive) */}
                        <TableCell sx={{ ...styles.td, textAlign: 'center' }}>
                          <Chip
                            label={candidate.isActive ? 'Active' : 'Inactive'}
                            size="small"
                            sx={{
                              backgroundColor: candidate.isActive ? THEME.successLight : '#F1F5F9',
                              color: candidate.isActive ? '#059669' : '#64748B',
                              fontWeight: 700,
                              fontSize: '0.72rem',
                              borderRadius: '6px',
                              height: 22
                            }}
                          />
                        </TableCell>

                        {/* Joining Date */}
                        <TableCell sx={styles.td}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CalendarIcon sx={{ fontSize: '1rem', color: THEME.secondary }} />
                            <Typography sx={{ fontSize: '0.85rem', color: THEME.primary, fontWeight: 500 }}>
                              {formatDisplayDate(candidate.joiningDate)}
                            </Typography>
                          </Stack>
                        </TableCell>

                        {/* Location */}
                        <TableCell sx={styles.td}>
                          {candidate.location ? (
                            <Chip
                              icon={<LocationIcon sx={{ fontSize: '0.9rem !important' }} />}
                              label={candidate.location}
                              size="small"
                              sx={{
                                backgroundColor: THEME.accentLight,
                                color: THEME.accentDark,
                                fontWeight: 600,
                                fontSize: '0.78rem',
                                borderRadius: '6px'
                              }}
                            />
                          ) : (
                            <Typography sx={{ color: THEME.secondary, fontSize: '0.85rem' }}>None</Typography>
                          )}
                        </TableCell>

                        {/* Academic Background (Compact HTML Cell) */}
                        <TableCell sx={{ ...styles.td, maxWidth: 240 }}>
                          <CompactHtmlCell
                            value={candidate.academicBackground}
                            title="Academic Background"
                            candidateName={candidate.name}
                            icon={SchoolIcon}
                            iconColor={THEME.purple}
                            onOpenDialog={handleOpenHtmlDialog}
                          />
                        </TableCell>

                        {/* Professional Experience (Compact HTML Cell) */}
                        <TableCell sx={{ ...styles.td, maxWidth: 260 }}>
                          <CompactHtmlCell
                            value={candidate.professionalExperience}
                            title="Professional Experience"
                            candidateName={candidate.name}
                            icon={WorkIcon}
                            iconColor={THEME.warning}
                            onOpenDialog={handleOpenHtmlDialog}
                          />
                        </TableCell>

                        {/* Communication Skills */}
                        <TableCell sx={{ ...styles.td, maxWidth: 240 }}>
                          {skills.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {skills.map((skill, idx) => (
                                <Chip
                                  key={skill.skillId || idx}
                                  label={skill.description || 'Skill'}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#F1F5F9',
                                    color: '#334155',
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    borderRadius: '6px'
                                  }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography sx={{ color: THEME.secondary, fontSize: '0.85rem' }}>None</Typography>
                          )}
                        </TableCell>

                        {/* Actions */}
                        <TableCell sx={{ ...styles.td, textAlign: 'center' }}>
                          <Tooltip title="View Candidate Dossier">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDetail(candidate)}
                              sx={{
                                color: THEME.accent,
                                backgroundColor: THEME.accentLight,
                                borderRadius: '8px',
                                p: 0.75,
                                '&:hover': { backgroundColor: '#DBEAFE' }
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* PAGINATION */}
            <TablePagination
              component="div"
              count={filteredCandidatesCount}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: `1px solid ${THEME.border}`,
                color: THEME.secondary,
                fontSize: '0.875rem'
              }}
            />
          </Box>
        ) : (
          /* GRID / CARDS VIEW */
          <Box>
            <Grid container spacing={2.5}>
              {paginatedData.map((candidate) => {
                const skills = candidate.communicationSkills || [];

                return (
                  <Grid item xs={12} sm={6} md={4} key={candidate.candidateId || candidate.name}>
                    <Card sx={styles.candidateCard}>
                      <CardContent sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Header Avatar & Details */}
                        <Stack direction="row" spacing={2} alignItems="center">
                          <CandidateAvatar candidate={candidate} size={54} fontSize="1.1rem" />
                          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: '1rem',
                                color: THEME.primary,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {candidate.name || 'Unnamed Candidate'}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                              {candidate.location && (
                                <Chip
                                  icon={<LocationIcon sx={{ fontSize: '0.85rem !important' }} />}
                                  label={candidate.location}
                                  size="small"
                                  sx={{
                                    backgroundColor: THEME.accentLight,
                                    color: THEME.accentDark,
                                    fontWeight: 600,
                                    fontSize: '0.72rem',
                                    height: 22
                                  }}
                                />
                              )}
                              <Chip
                                label={candidate.isActive ? 'Active' : 'Inactive'}
                                size="small"
                                sx={{
                                  backgroundColor: candidate.isActive ? THEME.successLight : '#F1F5F9',
                                  color: candidate.isActive ? '#059669' : '#64748B',
                                  fontWeight: 700,
                                  fontSize: '0.7rem',
                                  height: 22
                                }}
                              />
                            </Stack>
                          </Box>
                        </Stack>

                        <Divider sx={{ borderColor: THEME.border }} />

                        {/* Joining Date */}
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CalendarIcon sx={{ fontSize: '0.95rem', color: THEME.secondary }} />
                          <Typography sx={{ fontSize: '0.8rem', color: THEME.secondary }}>
                            Joined:{' '}
                            <strong style={{ color: THEME.primary }}>
                              {formatDisplayDate(candidate.joiningDate)}
                            </strong>
                          </Typography>
                        </Stack>

                        {/* Academic Background */}
                        {candidate.academicBackground && (
                          <Box
                            onClick={() =>
                              handleOpenHtmlDialog({
                                title: 'Academic Background',
                                candidateName: candidate.name,
                                content: candidate.academicBackground,
                                icon: SchoolIcon,
                                iconColor: THEME.purple
                              })
                            }
                            sx={{ cursor: 'pointer', '&:hover': { opacity: 0.85 } }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.72rem',
                                textTransform: 'uppercase',
                                color: THEME.secondary,
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mb: 0.3
                              }}
                            >
                              <SchoolIcon sx={{ fontSize: '0.9rem', color: THEME.purple }} />
                              Academic Background
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.82rem',
                                color: THEME.primary,
                                fontWeight: 500,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {stripHtml(candidate.academicBackground)}
                            </Typography>
                          </Box>
                        )}

                        {/* Professional Experience */}
                        {candidate.professionalExperience && (
                          <Box
                            onClick={() =>
                              handleOpenHtmlDialog({
                                title: 'Professional Experience',
                                candidateName: candidate.name,
                                content: candidate.professionalExperience,
                                icon: WorkIcon,
                                iconColor: THEME.warning
                              })
                            }
                            sx={{ cursor: 'pointer', '&:hover': { opacity: 0.85 } }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.72rem',
                                textTransform: 'uppercase',
                                color: THEME.secondary,
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mb: 0.3
                              }}
                            >
                              <WorkIcon sx={{ fontSize: '0.9rem', color: THEME.warning }} />
                              Experience
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.82rem',
                                color: THEME.primary,
                                fontWeight: 500,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {stripHtml(candidate.professionalExperience)}
                            </Typography>
                          </Box>
                        )}

                        {/* Communication Skills */}
                        {skills.length > 0 && (
                          <Box sx={{ mt: 'auto', pt: 1 }}>
                            <Typography
                              sx={{
                                fontSize: '0.72rem',
                                textTransform: 'uppercase',
                                color: THEME.secondary,
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                                mb: 0.5
                              }}
                            >
                              Communication Skills
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {skills.slice(0, 3).map((skill, idx) => (
                                <Chip
                                  key={skill.skillId || idx}
                                  label={skill.description}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#F1F5F9',
                                    color: '#334155',
                                    fontSize: '0.72rem',
                                    fontWeight: 500,
                                    height: 22
                                  }}
                                />
                              ))}
                              {skills.length > 3 && (
                                <Chip
                                  label={`+${skills.length - 3} more`}
                                  size="small"
                                  sx={{
                                    backgroundColor: THEME.accentLight,
                                    color: THEME.accent,
                                    fontSize: '0.72rem',
                                    fontWeight: 600,
                                    height: 22
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        )}
                      </CardContent>

                      <Divider sx={{ borderColor: THEME.border }} />

                      <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end', backgroundColor: '#FAFAFA' }}>
                        <Button
                          size="small"
                          endIcon={<VisibilityIcon fontSize="small" />}
                          onClick={() => handleOpenDetail(candidate)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            color: THEME.accent
                          }}
                        >
                          View Full Dossier
                        </Button>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* GRID PAGINATION */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <TablePagination
                component="div"
                count={filteredCandidatesCount}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[6, 12, 24, 48]}
                sx={{
                  backgroundColor: THEME.surface,
                  borderRadius: '12px',
                  border: `1px solid ${THEME.border}`,
                  color: THEME.secondary
                }}
              />
            </Box>
          </Box>
        )}

        {/* ========================================================= */}
        {/* DEDICATED HTML READER DIALOG (FOR BIG SECTIONS)           */}
        {/* ========================================================= */}
        <Dialog
          open={htmlDialog.open}
          onClose={handleCloseHtmlDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              border: `1px solid ${THEME.border}`,
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pb: 1.5,
              borderBottom: `1px solid ${THEME.border}`
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              {htmlDialog.icon && (
                <Box
                  sx={{
                    p: 1,
                    borderRadius: '10px',
                    backgroundColor: THEME.accentLight,
                    color: htmlDialog.iconColor || THEME.accent,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <htmlDialog.icon fontSize="small" />
                </Box>
              )}
              <Box>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: THEME.primary }}>
                  {htmlDialog.title}
                </Typography>
                {htmlDialog.candidateName && (
                  <Typography sx={{ fontSize: '0.8rem', color: THEME.secondary }}>
                    Candidate: <strong>{htmlDialog.candidateName}</strong>
                  </Typography>
                )}
              </Box>
            </Stack>
            <IconButton size="small" onClick={handleCloseHtmlDialog}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ py: 3, px: { xs: 2, md: 3 } }}>
            {htmlDialog.content ? (
              isHtml(htmlDialog.content) ? (
                <Box
                  sx={styles.richHtmlContent}
                  dangerouslySetInnerHTML={{ __html: htmlDialog.content }}
                />
              ) : (
                <Typography
                  sx={{
                    fontSize: '0.9rem',
                    lineHeight: 1.65,
                    color: '#334155',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {htmlDialog.content}
                </Typography>
              )
            ) : (
              <Typography sx={{ color: THEME.secondary, fontStyle: 'italic' }}>
                No details provided.
              </Typography>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2, borderTop: `1px solid ${THEME.border}` }}>
            <Button
              onClick={handleCloseHtmlDialog}
              variant="outlined"
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                borderColor: THEME.border,
                color: THEME.secondary
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* ========================================================= */}
        {/* CANDIDATE FULL DETAILS DOSSIER MODAL                      */}
        {/* ========================================================= */}
        <Dialog
          open={detailModalOpen}
          onClose={() => setDetailModalOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '20px',
              padding: '8px',
              border: `1px solid ${THEME.border}`,
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
            }
          }}
        >
          {selectedCandidate && (
            <>
              <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: THEME.primary }}>
                  Candidate Profile Dossier
                </Typography>
                <IconButton size="small" onClick={() => setDetailModalOpen(false)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </DialogTitle>

              <DialogContent sx={{ py: 2 }}>
                {/* Profile Header */}
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: '16px',
                    backgroundColor: THEME.tableHeader,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2.5,
                    mb: 3
                  }}
                >
                  <CandidateAvatar candidate={selectedCandidate} size={72} fontSize="1.6rem" />
                  <Box>
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: THEME.primary }}>
                      {selectedCandidate.name}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      {selectedCandidate.location && (
                        <Chip
                          icon={<LocationIcon sx={{ fontSize: '0.85rem !important' }} />}
                          label={selectedCandidate.location}
                          size="small"
                          sx={{
                            backgroundColor: THEME.accentLight,
                            color: THEME.accentDark,
                            fontWeight: 600,
                            fontSize: '0.75rem'
                          }}
                        />
                      )}
                      <Chip
                        label={selectedCandidate.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          backgroundColor: selectedCandidate.isActive ? THEME.successLight : '#F1F5F9',
                          color: selectedCandidate.isActive ? '#059669' : '#64748B',
                          fontWeight: 700,
                          fontSize: '0.75rem'
                        }}
                      />
                      <Chip
                        icon={<CalendarIcon sx={{ fontSize: '0.85rem !important' }} />}
                        label={`Joined: ${formatDisplayDate(selectedCandidate.joiningDate)}`}
                        size="small"
                        sx={{
                          backgroundColor: '#FFFFFF',
                          border: `1px solid ${THEME.border}`,
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      />
                    </Stack>
                  </Box>
                </Box>

                {/* Information Sections */}
                <Stack spacing={2.5}>
                  {/* Academic Background */}
                  <Box sx={{ p: 2.5, borderRadius: '12px', border: `1px solid ${THEME.border}`, backgroundColor: '#FFFFFF' }}>
                    <Typography
                      sx={{
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        color: THEME.purple,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1.5
                      }}
                    >
                      <SchoolIcon sx={{ fontSize: '1.1rem' }} />
                      Academic Background & Education
                    </Typography>
                    {selectedCandidate.academicBackground ? (
                      isHtml(selectedCandidate.academicBackground) ? (
                        <Box
                          sx={styles.richHtmlContent}
                          dangerouslySetInnerHTML={{ __html: selectedCandidate.academicBackground }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: '0.9rem', color: THEME.primary, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {selectedCandidate.academicBackground}
                        </Typography>
                      )
                    ) : (
                      <Typography sx={{ fontSize: '0.85rem', color: THEME.secondary, fontStyle: 'italic' }}>
                        No academic details specified
                      </Typography>
                    )}
                  </Box>

                  {/* Professional Experience */}
                  <Box sx={{ p: 2.5, borderRadius: '12px', border: `1px solid ${THEME.border}`, backgroundColor: '#FFFFFF' }}>
                    <Typography
                      sx={{
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        color: THEME.warning,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1.5
                      }}
                    >
                      <WorkIcon sx={{ fontSize: '1.1rem' }} />
                      Professional Experience & Background
                    </Typography>
                    {selectedCandidate.professionalExperience ? (
                      isHtml(selectedCandidate.professionalExperience) ? (
                        <Box
                          sx={styles.richHtmlContent}
                          dangerouslySetInnerHTML={{ __html: selectedCandidate.professionalExperience }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: '0.9rem', color: THEME.primary, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {selectedCandidate.professionalExperience}
                        </Typography>
                      )
                    ) : (
                      <Typography sx={{ fontSize: '0.85rem', color: THEME.secondary, fontStyle: 'italic' }}>
                        No professional experience specified
                      </Typography>
                    )}
                  </Box>

                  {/* Communication Skills */}
                  <Box sx={{ p: 2.5, borderRadius: '12px', border: `1px solid ${THEME.border}`, backgroundColor: '#FFFFFF' }}>
                    <Typography
                      sx={{
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        color: THEME.accent,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1.5
                      }}
                    >
                      <SkillIcon sx={{ fontSize: '1.1rem' }} />
                      Communication Skills & Languages
                    </Typography>
                    {(selectedCandidate.communicationSkills || []).length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {selectedCandidate.communicationSkills.map((skill, idx) => (
                          <Chip
                            key={skill.skillId || idx}
                            icon={<ChatIcon sx={{ fontSize: '0.85rem !important' }} />}
                            label={skill.description || 'Skill'}
                            sx={{
                              backgroundColor: THEME.accentLight,
                              color: THEME.accentDark,
                              fontWeight: 600,
                              fontSize: '0.82rem',
                              borderRadius: '8px'
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: '0.85rem', color: THEME.secondary, fontStyle: 'italic' }}>
                        No communication skills recorded
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setDetailModalOpen(false)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '8px',
                    borderColor: THEME.border,
                    color: THEME.secondary
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </LocalizationProvider >
  );
};

export default CandidateDetailsReport;
